import { MASK, normalizeBlobMask } from './tileset-source.js';
import { TILESET } from './tileset-source.js';
import { ART_PALETTE, ART_PALETTE_NAMES } from './art-palette.js';

let manifest;
let atlas;
const tileSelect = document.getElementById('tile-select');
const layerSelect = document.getElementById('layer-select');
const atlasImage = document.getElementById('atlas-image');
const variantSelect = document.getElementById('variant-select');
const animationFrameSelect = document.getElementById('animation-frame');
const pixelPreview = document.getElementById('pixel-preview');
const editorGuide = document.getElementById('editor-guide');
let selectedColor = 0;
let editablePixels;
let editableVariants = {};
let painting = false;
let activeTool = 'pencil';
let undoStack = [];
let redoStack = [];
let lastStitchedFrame = -1;

initialize().catch((error) => console.error('Failed to load tileset designer:', error));

async function initialize() {
  const [manifestResponse, loadedAtlas] = await Promise.all([
    fetch('/generated/tile-atlas.json'),
    loadImage('/generated/tile-atlas.png'),
  ]);
  if (!manifestResponse.ok) throw new Error('Could not load tile atlas manifest');
  manifest = await manifestResponse.json();
  atlas = loadedAtlas;
  atlasImage.src = atlas.src;

  for (const tile of manifest.tiles.filter((entry) => entry.channel !== 'backdrop')) {
    const option = document.createElement('option');
    option.value = tile.id;
    option.textContent = `${tile.id}: ${tile.name}`;
    tileSelect.append(option);
  }
  renderTileGallery();
  renderPaletteEditor();
  tileSelect.addEventListener('change', selectTile);
  layerSelect.addEventListener('change', renderSelection);
  variantSelect.addEventListener('change', () => { editablePixels = editableVariants[variantSelect.value]; drawEditablePixels(); });
  animationFrameSelect.addEventListener('change', loadEditablePixels);
  document.getElementById('editor-mode').addEventListener('change', updateEditorMode);
  document.getElementById('show-guide').addEventListener('change', (event) => { editorGuide.hidden = !event.currentTarget.checked; });
  document.getElementById('animate-stitched').addEventListener('change', () => { lastStitchedFrame = -1; });
  document.getElementById('grid-opacity').addEventListener('input', updateGridOpacity);
  window.addEventListener('resize', drawEditablePixels);
  pixelPreview.addEventListener('contextmenu', (event) => event.preventDefault());
  pixelPreview.addEventListener('pointerdown', (event) => {
    if (event.button !== 0 && event.button !== 2) return;
    painting = true;
    pixelPreview.setPointerCapture(event.pointerId);
    if (event.button === 2) pickPixel(event);
    else {
      pushUndo();
      paintPixel(event);
    }
  });
  pixelPreview.addEventListener('pointermove', (event) => { if (painting) paintPixel(event); });
  pixelPreview.addEventListener('pointerup', () => { painting = false; });
  pixelPreview.addEventListener('pointercancel', () => { painting = false; });
  document.getElementById('save-pixels').addEventListener('click', savePixels);
  document.getElementById('save-palette').addEventListener('click', saveWorldPalette);
  document.getElementById('undo').addEventListener('click', undo);
  document.getElementById('redo').addEventListener('click', redo);
  for (const button of document.querySelectorAll('[data-tool]')) button.addEventListener('click', () => setTool(button.dataset.tool));
  window.addEventListener('keydown', handleShortcut);
  setTool(activeTool);
  updateGridOpacity();
  selectTile();
  requestAnimationFrame(animateStitchedPreview);
}

function renderPaletteEditor() {
  const container = document.getElementById('palette-editor');
  container.replaceChildren(...ART_PALETTE.map((color, index) => {
    const label = document.createElement('label'); label.className = 'palette-color';
    const input = document.createElement('input'); input.type = 'color'; input.dataset.paletteIndex = index;
    input.value = color.slice(0, 7); input.disabled = index === 0;
    const text = document.createElement('span'); text.textContent = `${index} · ${ART_PALETTE_NAMES[index]}`;
    label.append(input, text); return label;
  }));
}

async function saveWorldPalette() {
  const status = document.getElementById('palette-save-status');
  const colors = [...document.querySelectorAll('[data-palette-index]')]
    .map((input, index) => index === 0 ? '#00000000' : `${input.value}ff`);
  status.textContent = 'Saving palette and rebuilding atlas…';
  try {
    const response = await fetch('/api/art-palette', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ colors }) });
    if (!response.ok) throw new Error(await response.text());
    status.textContent = 'Saved. Reloading…';
  } catch (error) { status.textContent = `Save failed: ${error.message}`; }
}

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });
}

function selectedTile() {
  return manifest.tiles.find((tile) => tile.id === Number(tileSelect.value));
}

function selectTile() {
  const tile = selectedTile();
  layerSelect.replaceChildren();
  for (const layer of Object.keys(tile.components)) {
    const option = document.createElement('option');
    option.value = layer;
    option.textContent = layer;
    layerSelect.append(option);
  }
  renderSelection();
  undoStack = [];
  redoStack = [];
  updateHistoryButtons();
  for (const card of document.querySelectorAll('.tile-card')) card.classList.toggle('selected', Number(card.dataset.tileId) === tile.id);
}

function renderTileGallery() {
  const gallery = document.getElementById('tile-gallery');
  gallery.replaceChildren(...manifest.tiles.filter((tile) => tile.channel !== 'backdrop').map((tile) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tile-card';
    button.dataset.tileId = tile.id;
    const canvas = document.createElement('canvas');
    canvas.width = 16; canvas.height = 16;
    const layer = Object.keys(tile.components)[0];
    drawFrame(canvas, tile.components[layer][0], 0, 0);
    const name = document.createElement('span');
    name.textContent = tile.name;
    button.append(canvas, name);
    button.addEventListener('click', () => { tileSelect.value = tile.id; selectTile(); });
    return button;
  }));
}

function renderSelection() {
  const tile = selectedTile();
  const layer = layerSelect.value;
  const masks = Object.keys(tile.components[layer]).map(Number).sort((a, b) => a - b);
  const frameCount = tile.animation?.kind === 'variants' ? tile.animation.frames.length : 1;
  animationFrameSelect.replaceChildren(...Array.from({ length: frameCount }, (_, frame) => {
    const option = document.createElement('option'); option.value = frame; option.textContent = `${frame + 1} of ${frameCount}`; return option;
  }));
  animationFrameSelect.disabled = frameCount === 1;
  document.getElementById('tile-metadata').innerHTML = `
    <dt>Key</dt><dd>${tile.key}</dd>
    <dt>Generated ID</dt><dd>${tile.id}</dd>
    <dt>Mode</dt><dd>${tile.mode}</dd>
    <dt>Channel</dt><dd>${tile.channel}</dd>
    <dt>Connection</dt><dd>${tile.connectionGroup}</dd>
    <dt>Frames</dt><dd>${masks.length}</dd>`;
  document.getElementById('variant-summary').textContent = `${masks.length} canonical variant${masks.length === 1 ? '' : 's'} on ${layer}.`;

  const previousMask = variantSelect.value;
  variantSelect.replaceChildren(...masks.map((mask) => {
    const option = document.createElement('option');
    option.value = mask;
    option.textContent = maskLabel(mask);
    return option;
  }));
  if (masks.includes(Number(previousMask))) variantSelect.value = previousMask;
  updateEditorMode(false);
  renderPalette();
  loadEditablePixels();
  renderStressMap(tile, layer);
  const grid = document.getElementById('variant-grid');
  grid.replaceChildren(...masks.map((mask) => variantCard(tile, layer, mask)));
}

function updateEditorMode(redraw = true) {
  const fullSet = document.getElementById('editor-mode').value === 'set';
  document.getElementById('variant-control').hidden = fullSet;
  if (redraw) drawEditablePixels();
}

function maskLabel(mask) {
  if (mask === 0) return 'Isolated — no neighbors';
  const names = [
    [MASK.N, 'North'], [MASK.E, 'East'], [MASK.S, 'South'], [MASK.W, 'West'],
    [MASK.NE, 'NE diagonal'], [MASK.SE, 'SE diagonal'],
    [MASK.SW, 'SW diagonal'], [MASK.NW, 'NW diagonal'],
  ];
  return names.filter(([bit]) => mask & bit).map(([, name]) => name).join(' + ');
}

function sourceTile() {
  return TILESET.find((tile) => tile.key === selectedTile().key);
}

function loadEditablePixels() {
  const tile = sourceTile();
  const frame = Number(animationFrameSelect.value || 0);
  const variants = tile.animation?.variantFrames?.[frame] ?? tile.components[layerSelect.value];
  editableVariants = Object.fromEntries(Object.entries(variants)
    .map(([mask, pixels]) => [mask, pixels.map((row) => [...row])]));
  editablePixels = editableVariants[variantSelect.value];
  drawEditablePixels();
  undoStack = [];
  redoStack = [];
  updateHistoryButtons();
}

function rgba(value) {
  return [1, 3, 5, 7].map((offset) => Number.parseInt(value.slice(offset, offset + 2), 16));
}

function drawEditablePixels() {
  editablePixels = editableVariants[variantSelect.value];
  const layout = editorLayout();
  pixelPreview.width = layout.columns * 16;
  pixelPreview.height = layout.rows * 16;
  const editorScale = updateEditorScale();
  editorGuide.width = pixelPreview.width * editorScale;
  editorGuide.height = pixelPreview.height * editorScale;
  const context = pixelPreview.getContext('2d');
  const palette = sourceTile().palette.map(rgba);
  context.clearRect(0, 0, pixelPreview.width, pixelPreview.height);
  for (const [index, mask] of layout.masks.entries()) {
    const pixels = editableVariants[mask];
    if (!pixels) continue;
    const image = context.createImageData(16, 16);
    for (let y = 0; y < 16; y++) for (let x = 0; x < 16; x++) {
      image.data.set(palette[pixels[y][x]], (y * 16 + x) * 4);
    }
    context.putImageData(image, index % layout.columns * 16, Math.floor(index / layout.columns) * 16);
  }
  drawGuide(layout, editorScale);
  if (editableVariants && Object.keys(editableVariants).length) {
    renderStressMap(selectedTile(), layerSelect.value);
  }
}

function updateEditorScale() {
  if (!pixelPreview.width) return 1;
  const section = pixelPreview.closest('section');
  const availableWidth = Math.max(pixelPreview.width, (section?.clientWidth ?? window.innerWidth) - 46);
  const scale = Math.max(1, Math.floor(availableWidth / pixelPreview.width));
  pixelPreview.style.width = `${pixelPreview.width * scale}px`;
  pixelPreview.parentElement.style.setProperty('--pixel-size', `${scale}px`);
  return scale;
}

function updateGridOpacity() {
  const opacity = Number(document.getElementById('grid-opacity').value) / 100;
  pixelPreview.parentElement.style.setProperty('--grid-opacity', String(opacity));
}

function editorLayout() {
  const allMasks = Object.keys(editableVariants).map(Number).sort((a, b) => a - b);
  const full = document.getElementById('editor-mode').value === 'set';
  const masks = full ? allMasks : [Number(variantSelect.value)];
  const columns = full ? sourceTile().mode === 'blob8' ? 8 : sourceTile().mode === 'pipe4' ? 4 : 1 : 1;
  return { masks, columns, rows: Math.ceil(masks.length / columns) };
}

function drawGuide(layout, scale) {
  const context = editorGuide.getContext('2d');
  context.clearRect(0, 0, editorGuide.width, editorGuide.height);
  context.strokeStyle = '#ff2438';
  context.fillStyle = '#ff2438';
  context.lineWidth = 4;
  context.lineCap = 'square';
  for (const [index, mask] of layout.masks.entries()) {
    const ox = index % layout.columns * 16 * scale;
    const oy = Math.floor(index / layout.columns) * 16 * scale;
    const centerX = ox + 8 * scale;
    const centerY = oy + 8 * scale;
    const right = ox + 16 * scale;
    const bottom = oy + 16 * scale;
    // Cardinal spokes meet at the center and continue cleanly into adjacent
    // variants. Diagonal connectivity is shown only by corner triangles.
    context.beginPath();
    if (mask & MASK.N) { context.moveTo(centerX, centerY); context.lineTo(centerX, oy); }
    if (mask & MASK.E) { context.moveTo(centerX, centerY); context.lineTo(right, centerY); }
    if (mask & MASK.S) { context.moveTo(centerX, centerY); context.lineTo(centerX, bottom); }
    if (mask & MASK.W) { context.moveTo(centerX, centerY); context.lineTo(ox, centerY); }
    context.stroke();
    const triangle = (points) => {
      context.beginPath();
      points.forEach(([x, y], point) => point
        ? context.lineTo(x, y)
        : context.moveTo(x, y));
      context.closePath();
      context.fill();
    };
    const triangleSize = Math.max(11, Math.min(16, scale * 1.25));
    if (mask & MASK.NE) triangle([[right,oy],[right-triangleSize,oy],[right,oy+triangleSize]]);
    if (mask & MASK.SE) triangle([[right,bottom],[right,bottom-triangleSize],[right-triangleSize,bottom]]);
    if (mask & MASK.SW) triangle([[ox,bottom],[ox+triangleSize,bottom],[ox,bottom-triangleSize]]);
    if (mask & MASK.NW) triangle([[ox,oy],[ox,oy+triangleSize],[ox+triangleSize,oy]]);
  }
}

function renderPalette() {
  const palette = document.getElementById('palette');
  const colors = sourceTile().palette;
  if (selectedColor >= colors.length) selectedColor = 0;
  palette.replaceChildren(...colors.map((color, index) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.title = `${index}: ${ART_PALETTE_NAMES[index] ?? 'Color'} — ${color}`;
    button.style.background = color;
    button.classList.toggle('selected', index === selectedColor);
    button.addEventListener('click', () => { selectedColor = index; renderPalette(); });
    return button;
  }));
}

function paintPixel(event) {
  const point = editablePoint(event);
  if (!point) return;
  const { x, y, pixels } = point;
  if (activeTool === 'eyedropper') return pickPixel(event);
  const color = activeTool === 'eraser' ? 0 : selectedColor;
  if (activeTool === 'fill') {
    if (pixels[y][x] === color) return;
    floodFill(pixels, x, y, pixels[y][x], color);
    painting = false;
  } else {
    if (pixels[y][x] === color) return;
    pixels[y][x] = color;
  }
  drawEditablePixels();
  document.getElementById('save-status').textContent = 'Unsaved changes';
}

function editablePoint(event) {
  const bounds = pixelPreview.getBoundingClientRect();
  const layout = editorLayout();
  const sheetX = Math.floor((event.clientX - bounds.left) * pixelPreview.width / bounds.width);
  const sheetY = Math.floor((event.clientY - bounds.top) * pixelPreview.height / bounds.height);
  const cellX = Math.floor(sheetX / 16); const cellY = Math.floor(sheetY / 16);
  const mask = layout.masks[cellY * layout.columns + cellX];
  if (mask === undefined) return null;
  return { x: sheetX % 16, y: sheetY % 16, mask, pixels: editableVariants[mask] };
}

function pickPixel(event) {
  const point = editablePoint(event);
  if (!point) return;
  selectedColor = point.pixels[point.y][point.x];
  renderPalette();
}

function floodFill(pixels, startX, startY, from, to) {
  const pending = [[startX, startY]];
  while (pending.length) {
    const [x, y] = pending.pop();
    if (x < 0 || x > 15 || y < 0 || y > 15 || pixels[y][x] !== from) continue;
    pixels[y][x] = to;
    pending.push([x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]);
  }
}

function pushUndo() {
  undoStack.push(cloneVariants(editableVariants));
  if (undoStack.length > 100) undoStack.shift();
  redoStack = [];
  updateHistoryButtons();
}

function undo() {
  if (!undoStack.length) return;
  redoStack.push(cloneVariants(editableVariants));
  editableVariants = undoStack.pop();
  drawEditablePixels();
  updateHistoryButtons();
  document.getElementById('save-status').textContent = 'Unsaved changes';
}

function redo() {
  if (!redoStack.length) return;
  undoStack.push(cloneVariants(editableVariants));
  editableVariants = redoStack.pop();
  drawEditablePixels();
  updateHistoryButtons();
  document.getElementById('save-status').textContent = 'Unsaved changes';
}

function cloneVariants(variants) {
  return Object.fromEntries(Object.entries(variants).map(([mask, pixels]) =>
    [mask, pixels.map((row) => [...row])]));
}

function updateHistoryButtons() {
  document.getElementById('undo').disabled = !undoStack.length;
  document.getElementById('redo').disabled = !redoStack.length;
}

function setTool(tool) {
  activeTool = tool;
  for (const button of document.querySelectorAll('[data-tool]')) button.setAttribute('aria-pressed', String(button.dataset.tool === tool));
}

function handleShortcut(event) {
  if (event.target.matches('input, select, textarea')) return;
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') { event.preventDefault(); savePixels(); return; }
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') { event.preventDefault(); event.shiftKey ? redo() : undo(); return; }
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'y') { event.preventDefault(); redo(); return; }
  const tools = { p: 'pencil', e: 'eraser', f: 'fill', i: 'eyedropper' };
  if (tools[event.key.toLowerCase()]) setTool(tools[event.key.toLowerCase()]);
}

async function savePixels() {
  const button = document.getElementById('save-pixels');
  const status = document.getElementById('save-status');
  button.disabled = true;
  status.textContent = 'Saving and rebuilding atlas…';
  try {
    const response = await fetch('/api/tile-override', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tileKey: selectedTile().key,
        layer: layerSelect.value,
        animationFrame: selectedTile().animation?.kind === 'variants' ? Number(animationFrameSelect.value) : null,
        variants: editableVariants,
      }),
    });
    if (!response.ok) throw new Error(await response.text());
    status.textContent = 'Saved. Reloading…';
  } catch (error) {
    button.disabled = false;
    status.textContent = `Save failed: ${error.message}. Run the designer with npm run dev.`;
  }
}

function drawFrame(canvas, frame, dx, dy, size = 16) {
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(atlas, frame.x, frame.y, frame.w, frame.h, dx, dy, size, size);
}

function variantCard(tile, layer, centerMask) {
  const card = document.createElement('div');
  card.className = 'variant-card';
  const canvas = document.createElement('canvas');
  canvas.width = 48;
  canvas.height = 48;
  const occupied = contextForMask(centerMask);
  drawOccupiedMap(canvas, tile, layer, occupied, 3, 3);
  const label = document.createElement('code');
  label.textContent = `mask ${centerMask}`;
  card.append(canvas, label);
  card.tabIndex = 0;
  card.addEventListener('click', () => { variantSelect.value = centerMask; editablePixels = editableVariants[variantSelect.value]; drawEditablePixels(); });
  return card;
}

function contextForMask(mask) {
  const occupied = new Set(['1,1']);
  const directions = [
    [MASK.N, 1, 0], [MASK.E, 2, 1], [MASK.S, 1, 2], [MASK.W, 0, 1],
    [MASK.NE, 2, 0], [MASK.SE, 2, 2], [MASK.SW, 0, 2], [MASK.NW, 0, 0],
  ];
  for (const [bit, x, y] of directions) if (mask & bit) occupied.add(`${x},${y}`);
  return occupied;
}

function maskAt(occupied, x, y, mode) {
  if (mode === 'plain') return 0;
  let mask = 0;
  const checks = [
    [MASK.N, x, y - 1], [MASK.E, x + 1, y], [MASK.S, x, y + 1], [MASK.W, x - 1, y],
    [MASK.NE, x + 1, y - 1], [MASK.SE, x + 1, y + 1],
    [MASK.SW, x - 1, y + 1], [MASK.NW, x - 1, y - 1],
  ];
  for (const [bit, nx, ny] of checks) if (occupied.has(`${nx},${ny}`)) mask |= bit;
  return mode === 'blob8' ? normalizeBlobMask(mask) : mask & 15;
}

function drawOccupiedMap(canvas, tile, layer, occupied, width, height) {
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = false;
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    if (!occupied.has(`${x},${y}`)) continue;
    const frame = tile.components[layer][maskAt(occupied, x, y, tile.mode)];
    drawFrame(canvas, frame, x * 16, y * 16);
  }
}

function renderStressMap(tile, layer, variants = editableVariants) {
  const occupied = new Set();
  const masks = Object.keys(variants).map(Number).sort((a, b) => a - b);
  const patchColumns = tile.mode === 'blob8' ? 8 : tile.mode === 'pipe4' ? 4 : 1;
  const patchSize = tile.mode === 'plain' ? 1 : 4;
  const width = patchColumns * patchSize;
  const height = Math.ceil(masks.length / patchColumns) * patchSize;
  for (const [index, mask] of masks.entries()) {
    const offsetX = index % patchColumns * patchSize;
    const offsetY = Math.floor(index / patchColumns) * patchSize;
    if (tile.mode === 'plain') occupied.add(`${offsetX},${offsetY}`);
    else {
      for (const coordinate of contextForMask(mask)) {
        const [x, y] = coordinate.split(',').map(Number);
        occupied.add(`${offsetX + x},${offsetY + y}`);
      }
    }
  }
  const canvas = document.getElementById('stress-preview');
  canvas.width = width * 16;
  canvas.height = height * 16;
  const context = canvas.getContext('2d');
  const palette = sourceTile().palette.map(rgba);
  context.clearRect(0, 0, canvas.width, canvas.height);
  for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
    if (!occupied.has(`${x},${y}`)) continue;
    const wantedMask = maskAt(occupied, x, y, tile.mode);
    const availableMasks = Object.keys(variants).map(Number);
    const mask = variants[wantedMask] ? wantedMask : closestMask(availableMasks, wantedMask);
    const pixels = variants[mask];
    const image = context.createImageData(16, 16);
    for (let py = 0; py < 16; py++) for (let px = 0; px < 16; px++) {
      image.data.set(palette[pixels[py][px]], (py * 16 + px) * 4);
    }
    context.putImageData(image, x * 16, y * 16);
  }
}

function animateStitchedPreview(timestamp) {
  const tile = sourceTile();
  const animatedFrames = tile?.animation?.variantFrames;
  const shouldAnimate = document.getElementById('animate-stitched').checked && animatedFrames?.length > 1;
  const selectedFrame = Number(animationFrameSelect.value || 0);
  const frame = shouldAnimate
    ? Math.floor(timestamp / tile.animation.frameDurationMs) % animatedFrames.length
    : selectedFrame;
  if (frame !== lastStitchedFrame) {
    const variants = frame === selectedFrame ? editableVariants : animatedFrames?.[frame] ?? editableVariants;
    renderStressMap(selectedTile(), layerSelect.value, variants);
    lastStitchedFrame = frame;
  }
  requestAnimationFrame(animateStitchedPreview);
}

function closestMask(masks, wanted) {
  const bitCount = (value) => { let count = 0; for (; value; value >>>= 1) count += value & 1; return count; };
  return masks.reduce((best, candidate) =>
    bitCount(candidate ^ wanted) < bitCount(best ^ wanted) ? candidate : best, masks[0]);
}
